import axios from 'axios';

import { API_AUTH_REFRESH_URL, API_URL } from '../utils';
import { clearSessionToken, getRefreshCredential, getSessionToken, readKeptSession, setSessionToken, whenSessionKept } from '../utils/session';
import { awaitClearance, noteError, noteResponse } from './governor';
import { planLimitOf, planLimitSentence } from '../utils/planLimit';

/**
 * Shared API client.
 *
 * - Attaches the session's bearer token (utils/session.js, held in memory
 *   since KPR-1669) to every request.
 * - On 401, redirects to the login page with a returnPath so the user
 *   comes back to where they were after signing in — from a protected page
 *   only (`shouldRedirectToLogin` below). A public page is never left on a
 *   refused request; one readable signed out forgets a refused token in place
 *   (`endsSessionInPlace`).
 * - In the phone app, renews a device session (KPR-1674): a 401 on a request
 *   that carried the session's 1-hour token exchanges the refresh credential
 *   once (`renewSession`, single-flight: parallel 401s share one exchange)
 *   and retries the request once. Only a REFUSED renewal ends the session,
 *   by the redirect rules above. The web holds no refresh credential, so none
 *   of this runs there.
 * - Honours "slow down" (src/api/governor.js): a 429 on a read that names its
 *   wait, or a run of no-answer failures (a WAF block, or keepr out of
 *   reach), pauses new GETs — they wait, they do not fail. Other methods go
 *   through. Nothing is retried here.
 *
 * Components should import { api } from here instead of using axios
 * directly, and should not build Authorization headers themselves.
 */
export const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(config => {
	const token = getSessionToken();
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	return config;
});

// Registered second, so it runs FIRST (axios runs request interceptors in
// reverse): a GET held through a pause reads the token after its wait, not
// the one that was current when it was asked for. It also stamps the moment
// each request leaves, so failures in flight together count as one.
api.interceptors.request.use(awaitClearance);

let redirectingToLogin = false;

// Pages that own their signed-out state: the auth pages themselves, the
// public landing pages the API mails/links out (share codes, email
// verification, password reset, the mail's preferences link), and /logout — which has just dropped the
// token, and must never become a returnPath (signing in would land on it and
// sign straight back out). A 401 never redirects away from one of these.
const AUTH_PATH_PREFIXES = [
	'/login',
	'/register',
	'/s/',
	'/verify-email',
	'/forgot-password',
	'/reset-password',
	// The preferences link in the foot of every mail: session-less by design
	// (it only turns things off), and often opened in a browser holding
	// somebody's expired session.
	'/email-preferences',
];
const AUTH_PATHS = ['/logout'];

// Pages a visitor with no account may READ: Home (the sign-in form when
// signed out) and the documentation portal's guides. A 401 never redirects
// away from these either. One that carried NO token is a visitor who never
// signed in — nothing to do. One that carried a token is a session the API no
// longer accepts (expired, revoked, signed out everywhere): it ENDS HERE, in
// place — the token is forgotten and the page re-renders signed out (Home as
// its sign-in form, a guide with "Sign in" in the rail) — rather than a hard
// hop to /login that throws away what the person was reading.
const readableSignedOut = pathname => pathname === '/'
	|| pathname === '/status'
	|| pathname === '/docs'
	|| pathname.startsWith('/docs/');

// A permanent public form's address, `/f/<collection-slug>/<form-slug>`
// (KPR-513): a stranger's page, like a share code. EXACTLY two segments —
// `/f/<code>` is a multi add's short link, a signed-in person's page, and a
// 401 there must still send them to sign in, so this is not a `/f/` prefix.
const PUBLIC_FORM_PATH = /^\/f\/[^/]+\/[^/]+\/?$/;

const isAuthPage = pathname => AUTH_PATHS.includes(pathname)
	|| AUTH_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))
	|| PUBLIC_FORM_PATH.test(pathname);

const authorizationOf = config => {
	const headers = config?.headers;
	if (!headers) return '';
	return (typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization) || '';
};

// Did the refused request carry the token the session holds NOW? Only then
// does its refusal speak for the session: a request sent with an older token
// can answer after a sign-in has written a new one, and forgetting whatever
// is current then signed the NEW session out (the review of the live-pass
// merges). A refusal speaks only for the token it refused.
const carriedCurrentToken = config => {
	const authorization = authorizationOf(config);
	if (!authorization) return false;
	const current = getSessionToken();
	return Boolean(current) && authorization === `Bearer ${current}`;
};

/**
 * Does this 401 send the person to sign in? Never from a PUBLIC page — one
 * that owns its signed-out state, or one readable signed out: a refused
 * request is not a reason to leave a page nobody needs a session for (the
 * live pass: a dead intake link bounced to /login). Yes from every other page
 * — a protected page asked for without a session, or a session the API no
 * longer accepts.
 *
 * The first line of defense is that nothing asks for an authenticated read
 * without a token (every app-wide provider and shell hook gates its load on
 * utils/session.js — src/__tests__/signedOutRoutes.test.js holds them to it);
 * this rule is what keeps a stray one from bouncing a reader to /login. The
 * 404 page is not listed: any URL can be one, so it is covered by that first
 * rule alone — the shell asks nothing around it while signed out.
 */
export function shouldRedirectToLogin(pathname) {
	if (isAuthPage(pathname)) return false;
	if (readableSignedOut(pathname)) return false;
	return true;
}

/**
 * Does this 401 end the session where the person stands? On a page readable
 * signed out, when the refused request carried the token the session still holds:
 * yes — the API has refused that token, so every later read would be refused
 * too. Not when it carried an OLDER token (a late answer after a sign-in):
 * the session held now was never refused. Not on the auth pages: a
 * wrong password on /login is a 401 about the PASSWORD, and a still-valid
 * session must survive it.
 */
export function endsSessionInPlace(pathname, config) {
	return !isAuthPage(pathname) && readableSignedOut(pathname) && carriedCurrentToken(config);
}

/**
 * Send the person to sign in, and back to `returnPath` (a path in this app,
 * with its query string) afterwards. A full page load, not an in-app
 * navigation: whatever the last session left in memory goes with it. Once
 * only — a real redirect leaves the page, so a second caller (the route
 * guard and a 401 landing together) has nothing to add.
 *
 * The two callers: the 401 interceptor below (a token the API rejects) and
 * the route guard, components/common/RequireSession.js (no session at all —
 * no token, or one whose `exp` has passed — on a protected page, before the
 * page renders).
 */
export function redirectToLogin(returnPath) {
	if (redirectingToLogin) return;
	redirectingToLogin = true;
	window.location.assign(`/login?isRedirect=true&returnPath=${encodeURIComponent(returnPath)}`);
}

// The device-session refresh (KPR-1674; keepr-api SCHEMA.md § Device
// sessions). A bare instance: the exchange carries no bearer and must never
// meet the 401 handler below, which would renew the renewal.
const refreshClient = axios.create({ baseURL: API_URL });

// Requests a 401 never renews: a refused sign-in is about the password, and
// a refused refresh is the answer itself.
const NEVER_RENEWED = /\/(login(\/mfa)?|auth\/refresh)\/?(\?|$)/;

// The one exchange in flight, for the credential it spends.
let renewal = null;

/**
 * Exchange the held refresh credential for a new pair — once per credential,
 * one exchange at a time: every caller while one is in flight gets the same
 * promise (a credential is dead the moment it is used, and presenting it
 * twice is a replay that signs the device out). Resolves to:
 *
 *   'renewed'      a new pair is held and kept: retry with it
 *   'refused'      401/400 `refresh_invalid`: the device is signed out
 *   'raced'        409 `refresh_raced` with no newer pair kept: the session
 *                  lives, but this request cannot be retried
 *   'unreachable'  no answer, 5xx, 429: offline — nothing ends
 *   'stale'        the session changed while it was asked (a sign-out, or
 *                  another sign-in): its answer is not this session's
 *   'none'         nothing to renew (the web)
 *
 * The new credential is written to the secure store before anything uses it
 * (`whenSessionKept`). A lost answer is NOT retried with the spent
 * credential: the owner ruled a replay ends the session, with no grace
 * window (knowledge rule device-refresh-strict-ruling).
 */
export function renewSession() {
	const credential = getRefreshCredential();
	if (!credential) return Promise.resolve('none');
	if (renewal && renewal.credential === credential) return renewal.promise;
	const stillHeld = () => getRefreshCredential() === credential;
	const promise = (async () => {
		try {
			const { data } = await refreshClient.post(API_AUTH_REFRESH_URL, { refreshToken: credential });
			if (!stillHeld()) return 'stale';
			if (!data?.token || !data?.refreshToken) return 'unreachable';
			setSessionToken(data.token, { refreshToken: data.refreshToken });
			await whenSessionKept();
			return 'renewed';
		} catch (error) {
			const status = error?.response?.status;
			if (!stillHeld()) return 'stale';
			if (status === 409) {
				// Another exchange won with this credential. Its pair is the
				// newest; take it only if it was kept, never resend ours.
				const kept = await readKeptSession();
				if (!stillHeld()) return 'stale';
				if (kept.token && kept.refreshToken && kept.refreshToken !== credential) {
					setSessionToken(kept.token, { refreshToken: kept.refreshToken });
					return 'renewed';
				}
				return 'raced';
			}
			if (status === 401 || status === 400) return 'refused';
			return 'unreachable';
		}
	})();
	renewal = { credential, promise };
	promise.finally(() => { if (renewal?.promise === promise) renewal = null; });
	return promise;
}

/** May this refused request be renewed and retried? Only once, only a device session's, never a sign-in's. */
function mayRenew(config) {
	if (!config || config.keeprRenewed) return false;
	if (!getRefreshCredential()) return false;
	if (!authorizationOf(config)) return false;
	return !NEVER_RENEWED.test(String(config.url || ''));
}

function retryWithSession(config) {
	config.keeprRenewed = true;
	// The request interceptor writes the session's bearer again; one that has
	// none must not resend the old.
	const headers = config.headers;
	if (headers && typeof headers.delete === 'function') headers.delete('Authorization');
	else if (headers) delete headers.Authorization;
	return api.request(config);
}

// What a 401 does when the session is over: today's redirect rules.
function endRefusedSession(error) {
	const { pathname, search } = window.location;
	if (shouldRedirectToLogin(pathname)) redirectToLogin(`${pathname}${search}`);
	else if (endsSessionInPlace(pathname, error.config)) clearSessionToken();
}

async function renewOrEnd(error) {
	const config = error.config;
	// Answered after a renewal already replaced the token it carried: retry
	// with the session's own, no second exchange.
	if (!carriedCurrentToken(config) && getSessionToken()) return retryWithSession(config);
	const outcome = await renewSession();
	if (outcome === 'renewed') return retryWithSession(config);
	if (outcome === 'refused') {
		// The device is signed out: the credential is dead. Forget it here,
		// then send the person on as any refused session is.
		clearSessionToken();
		if (!redirectingToLogin) {
			const { pathname, search } = window.location;
			if (shouldRedirectToLogin(pathname)) redirectToLogin(`${pathname}${search}`);
		}
	}
	return Promise.reject(rewordPlanLimit(error));
}

api.interceptors.response.use(
	response => response,
	error => {
		if (error?.response?.status === 401 && !redirectingToLogin) {
			if (mayRenew(error.config)) return renewOrEnd(error);
			endRefusedSession(error);
		}
		// A plan limit gets the web's words before any caller sees it
		// (rewordPlanLimit, below); every other error passes as it came.
		return Promise.reject(rewordPlanLimit(error));
	}
);

// Registered after the 401 handler, which stays exactly as it is: this one
// only watches, and passes every answer and every error on untouched.
api.interceptors.response.use(
	response => {
		noteResponse(response);
		return response;
	},
	error => {
		noteError(error);
		return Promise.reject(error);
	}
);

/**
 * A plan limit, said the web's way wherever it lands (docs/platform-standards.md
 * §7 "Plan limits"). Every surface that can meet a limit says the server's
 * sentence in some form — a toast, a field, an inline Alert — so the one
 * refusal is worded once, here: `data.message` becomes the words for whoever
 * met it (owner, member or visitor, read from what the API chose to send),
 * and the API's own sentence stays as `data.apiMessage`. Nothing else about
 * the error changes; a surface that draws the `PlanLimitAlert` reads the
 * payload as it came. Called from the first response handler above, so the
 * handler count stays what the governor's test pins.
 */
export function rewordPlanLimit(error) {
	const refusal = planLimitOf(error);
	if (refusal && refusal.apiMessage === undefined) {
		refusal.apiMessage = refusal.message;
		refusal.message = planLimitSentence(refusal);
	}
	return error;
}

/** Tests only: the redirect latch (a real redirect leaves the page, so it never resets). */
export function _resetLoginRedirect() {
	redirectingToLogin = false;
}

/** Tests only: forget an exchange in flight. */
export function _resetRenewal() {
	renewal = null;
}

/**
 * The total a list endpoint reports in its `X-Total-Count` header, or
 * `fallback` — usually the page's own length (or null where "unknown" is the
 * honest answer) — when the header is missing or unreadable. Axios lowercases
 * header names, but a proxy in front of the API may not, so the key is found
 * whatever its case. Every paged/filtered list reads its count through here so
 * the header name and the fallback rule are one rule.
 */
export const readTotalCount = (responseHeaders, fallback) => {
	if (!responseHeaders) return fallback;
	let raw = responseHeaders['x-total-count'];
	if (raw === undefined) {
		const key = Object.keys(responseHeaders).find(name => name.toLowerCase() === 'x-total-count');
		raw = key === undefined ? undefined : responseHeaders[key];
	}
	const parsed = Number(raw);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export default api;
